import { useQuery } from "@tanstack/react-query";
import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import axios from "axios";
import { Mail, PenLine } from "lucide-react";
import { Asterisk } from "@/components/Brand";
import NavigationHeader from "@/components/NavigationHeader";
import { fadeUp, stagger } from "@/lib/motion";
import CommentSection from "@/components/CommentSection";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * Dear Glowgetters — Morgan's weekly website-exclusive video letters.
 * Each segment: a short story with a lesson at the end. Lives nowhere else.
 */
export default function DearGlowgettersPage() {
  const { data: segments } = useQuery({
    queryKey: ["glowgetters"],
    queryFn: async () => {
      try {
        const { data } = await axios.get(`${API}/glowgetters`);
        return data;
      } catch {
        return [];
      }
    },
    initialData: [],
    initialDataUpdatedAt: 0,
  });

  return (
    <div className="relative min-h-screen bg-ink font-body text-white">
      <NavigationHeader />
      <header className="border-b border-white/10 px-4 pb-14 pt-36 sm:px-6 lg:px-8 lg:pt-44">
        <div className="mx-auto max-w-5xl">
          <motion.p
            initial={{ opacity: 0, y: 14 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="mb-4 inline-flex items-center gap-2 text-[11px] font-bold uppercase tracking-[0.24em] text-cherry"
          >
            <Asterisk className="h-3.5 w-3.5" /> City Girls Decoded Exclusive
          </motion.p>
          <motion.h1
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.1 }}
            data-testid="glowgetters-title"
            className="font-display text-4xl font-extrabold tracking-tight sm:text-5xl lg:text-6xl"
          >
            Dear{" "}
            <span className="font-garamond italic font-medium text-cherry">
              Glowgetters,
            </span>
          </motion.h1>
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.2 }}
            className="mt-5 max-w-xl text-base leading-relaxed text-white/65 sm:text-lg"
          >
            A weekly video letter from Morgan — a short story, straight to
            you, with the lesson I pulled from it at the end. You can't get
            this on the feeds, on YouTube, or anywhere else. Glowgetters only.
          </motion.p>
        </div>
      </header>

      <main className="px-4 py-16 sm:px-6 lg:px-8 lg:py-20">
        <div className="mx-auto max-w-3xl space-y-10">
          {segments.length === 0 ? (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.7, delay: 0.25 }}
              data-testid="glowgetters-empty"
              className="rounded-3xl border border-dashed border-white/20 bg-panel/60 p-10 text-center sm:p-14"
            >
              <PenLine className="mx-auto h-8 w-8 text-cherry" />
              <h2 className="mt-5 font-display text-2xl font-extrabold sm:text-3xl">
                The first letter is on my desk.
              </h2>
              <p className="mx-auto mt-4 max-w-md text-base leading-relaxed text-white/65">
                Every week I'm dropping a segment here that lives nowhere else
                — a short story, told like only we tell them, and the lesson I
                pulled from it at the end.
              </p>
              <p className="mt-5 text-sm font-bold uppercase tracking-[0.18em] text-cherry">
                Glowgetters only · New segments drop weekly
              </p>
              <Link
                data-testid="glowgetters-join-cta"
                to="/"
                className="mt-8 inline-flex items-center gap-3 rounded-full bg-cherry px-7 py-4 text-sm font-bold uppercase tracking-[0.12em] text-white transition-all duration-300 hover:scale-[1.04] hover:bg-cherry-soft hover:shadow-[0_0_40px_rgba(255,0,85,0.45)]"
              >
                <Mail className="h-4 w-4" /> Get the drop first — join the Glowgetters
              </Link>
            </motion.div>
          ) : (
            segments.map((seg, i) => (
              <motion.article
                key={seg.slug || i}
                variants={stagger}
                initial="hidden"
                whileInView="show"
                viewport={{ once: true, amount: 0.15 }}
                data-testid={`glowgetter-segment-${seg.number || i + 1}`}
                className="rounded-3xl border border-white/10 bg-panel p-6 sm:p-9"
              >
                <motion.p
                  variants={fadeUp}
                  className="text-[11px] font-bold uppercase tracking-[0.22em] text-cherry"
                >
                  Segment {String(seg.number || i + 1).padStart(2, "0")} · {seg.date}
                </motion.p>
                <motion.h2
                  variants={fadeUp}
                  className="mt-3 font-display text-2xl font-extrabold tracking-tight sm:text-3xl"
                >
                  {seg.title}
                </motion.h2>
                <motion.div variants={fadeUp} className="mt-6">
                  <video
                    data-testid={`glowgetter-video-${seg.number || i + 1}`}
                    src={seg.video_url}
                    poster={seg.poster_url || undefined}
                    controls
                    preload="none"
                    playsInline
                    className="w-full rounded-2xl border border-white/10 bg-black"
                  />
                </motion.div>
                <motion.p
                  variants={fadeUp}
                  className="mt-6 whitespace-pre-line text-base leading-relaxed text-white/75 sm:text-lg"
                >
                  {seg.story}
                </motion.p>
                <motion.div
                  variants={fadeUp}
                  data-testid={`glowgetter-lesson-${seg.number || i + 1}`}
                  className="mt-6 rounded-2xl border border-cherry/30 bg-cherry/[0.07] p-6"
                >
                  <p className="text-[11px] font-bold uppercase tracking-[0.22em] text-cherry">
                    The lesson
                  </p>
                  <p className="mt-3 whitespace-pre-line font-garamond text-xl italic leading-snug text-white/90 sm:text-2xl">
                    {seg.lesson}
                  </p>
                </motion.div>
                <motion.div
                  variants={fadeUp}
                  className="mt-8"
                  data-testid={`glowgetter-comments-${seg.number || i + 1}`}
                >
                  <CommentSection
                    articleType="glowgetters"
                    slug={seg.slug}
                    accent="#FF0055"
                    onAccent="#FFFFFF"
                  />
                </motion.div>
              </motion.article>
            ))
          )}
        </div>
      </main>
    </div>
  );
}
